import React, { useEffect, useState } from "react";
import { Message } from "../Types/Types";
import { ChatHeader } from "./ChatHeader";
import { ChatMessage } from "../ChatRow/ChatMessage";
import { ChatInput } from "./ChatInput";

interface ChatContainerProps {
  pdfSummary?: string[] | string;
  fileName?: string;
}

const dummyMessages: Message[] = [
  {
    content: "👋 Hello! Upload a document to start chatting with it.",
    sender: "ai",
    timestamp: new Date().toISOString(),
  },
];

export const ChatContainer: React.FC<ChatContainerProps> = ({
  pdfSummary,
  fileName,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (pdfSummary && pdfSummary.length > 0) {
      let summaryContent = "";
      if (Array.isArray(pdfSummary)) {
        summaryContent = pdfSummary.join("\n\n");
      } else if (typeof pdfSummary === "string") {
        summaryContent = pdfSummary;
      }

      const summaryMessage: Message = {
        content: `${summaryContent}`,
        sender: "ai",
        timestamp: new Date().toISOString(),
      };
      setMessages([summaryMessage]);
    } else {
      setMessages(dummyMessages);
    }
  }, [pdfSummary, fileName]);

  const handleSend = async (content: string) => {
    const newMessage: Message = {
      content,
      sender: "user",
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, newMessage]);
    setIsLoading(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      window.open("https://app.kroolo.com/login", "_blank");
      const redirectMessage: Message = {
        content:
          "🔗 Redirecting you to Kroolo login page to continue the conversation...",
        sender: "ai",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, redirectMessage]);
    } catch (error) {
      console.error("Error:", error);
      const errorMessage: Message = {
        content:
          "Sorry, there was an error processing your request. Please try again.",
        sender: "ai",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = async () => {
    const lastUserMessage = [...messages]
      .reverse()
      .find((m) => m.sender === "user");
    if (lastUserMessage) {
      setMessages((prev) => prev.slice(0, -1));
      await handleSend(lastUserMessage.content);
    }
  };

  const handleClear = () => {
    if (pdfSummary) {
      let summaryContent = "";
      if (Array.isArray(pdfSummary)) {
        summaryContent = pdfSummary.join("\n\n");
      } else if (typeof pdfSummary === "string") {
        summaryContent = pdfSummary;
      }

      if (summaryContent) {
        const summaryMessage: Message = {
          content: `${summaryContent}`,
          sender: "ai",
          timestamp: new Date().toISOString(),
        };
        setMessages([summaryMessage]);
      } else {
        setMessages([]);
      }
    } else {
      setMessages([]);
    }
  };

  return (
    <div className="flex h-full grow flex-col rounded-lg border border-[#e1e1e1]">
      <ChatHeader onClear={handleClear} />
      <div className="thin-scrollbar flex-grow overflow-y-auto p-3">
        {messages.map((message, index) => (
          <ChatMessage
            key={message.timestamp}
            message={message}
            isLastMessage={
              index === messages.length - 1 && message.sender === "ai"
            }
            onRegenerate={handleRegenerate}
            index={index}
          />
        ))}
        {isLoading && (
          <div className="px-4 py-2 text-gray-500">
            <span className="animate-pulse">AI is thinking...</span>
          </div>
        )}
      </div>
      <ChatInput
        onSend={handleSend}
        onExtract={() => console.log("Extract clicked")}
      />
    </div>
  );
};

export default ChatContainer;
